const DEFAULT_CTA_BACKGROUND_RGB = { r: 39, g: 39, b: 39 };
const DEFAULT_CTA_BACKGROUND_ALPHA = 0.8;
// 管理画面のコンテンツ作成は既定16pxを送る（admin-front の DEFAULT_CTA_FONT_SIZE）。
// cta_font_size が null のcontent（CTA未設定で作られた古いデータ等）でも管理画面と
// 食い違わないよう、CSS既定値も16pxに揃える。
const DEFAULT_CTA_FONT_SIZE_PX = 16;

const normalizeHexColor = (value) => {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const hex = raw.startsWith('#') ? raw : `#${raw}`;
  const normalized = hex.replace(/^#([0-9a-fA-F]{3})$/, (_, shortHex) => {
    return `#${shortHex.split('').map(ch => `${ch}${ch}`).join('')}`;
  });
  return /^#[0-9a-fA-F]{6}$/.test(normalized) ? normalized : null;
};

const hexToRgb = (hex) => {
  const normalized = normalizeHexColor(hex);
  if (!normalized) return null;
  return {
    r: parseInt(normalized.slice(1, 3), 16),
    g: parseInt(normalized.slice(3, 5), 16),
    b: parseInt(normalized.slice(5, 7), 16),
  };
};

const normalizeBackgroundAlpha = (value) => {
  if (value == null) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  return Math.min(100, Math.max(0, parsed)) / 100;
};

const normalizeFontSizePx = (value) => {
  if (value == null) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return `${parsed}px`;
};

const resolveCtaBackgroundColor = (content) => {
  const hasBackgroundColor = content?.ctaBackgroundColorCode != null;
  const hasBackgroundOpacity = content?.ctaBackgroundOpacity != null;
  if (!hasBackgroundColor && !hasBackgroundOpacity) return null;

  const rgb = hexToRgb(content?.ctaBackgroundColorCode) || DEFAULT_CTA_BACKGROUND_RGB;
  const alpha = normalizeBackgroundAlpha(content?.ctaBackgroundOpacity) ?? DEFAULT_CTA_BACKGROUND_ALPHA;

  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
};

export const applyCtaStyleToElements = (content, frameEl, textEl) => {
  if (!frameEl || !textEl) return;

  const backgroundColor = resolveCtaBackgroundColor(content);
  const textColor = normalizeHexColor(content?.ctaTextColorCode);
  const fontSize = normalizeFontSizePx(content?.ctaFontSize);

  // API値がnullの時は既存デフォルトスタイルへフォールバックさせる
  if (backgroundColor) {
    frameEl.style.setProperty('--storeeel-cta-background', backgroundColor);
  } else {
    frameEl.style.removeProperty('--storeeel-cta-background');
  }

  if (textColor) {
    frameEl.style.setProperty('--storeeel-cta-text-color', textColor);
    textEl.style.setProperty('--storeeel-cta-text-color', textColor);
  } else {
    frameEl.style.removeProperty('--storeeel-cta-text-color');
    textEl.style.removeProperty('--storeeel-cta-text-color');
  }

  if (fontSize) {
    textEl.style.setProperty('--storeeel-cta-font-size', fontSize);
  } else {
    textEl.style.removeProperty('--storeeel-cta-font-size');
  }
};

export const applyCtaLinkIconVisibility = (ctaLink, frameEl) => {
  if (!frameEl) return;
  const hasCtaLink = Boolean(String(ctaLink || '').trim());
  // CTAリンクがない場合は「>」アイコンを非表示にする
  frameEl.dataset.hasCtaLink = hasCtaLink ? 'true' : 'false';
};

export const ctaButton = (content) => {
  const ctaText = content?.ctaText || '';
  const ctaLink = content?.ctaLink || '';
  if (!ctaText && !window.storeeelItems.isMobile) {
    // PC で CTA テキストがない場合は何も返さない
    return null;
  }

  const shouldHideUntilMediaReady = Boolean(
    window.storeeelItems?.isMobile
    && window.storeeelItems?.frame !== 'square'
    && ctaText
  );

  // 先方サイトの汎用CSSから影響を受けないよう Shadow DOM で隔離
  const host = document.createElement('storeeel-cta-host');
  host.setAttribute('data-storeeel-cta-host', '');
  if (window.storeeelItems?.isMobile) {
    host.style.setProperty('--storeeel-cta-left', '16px');
    host.style.setProperty('--storeeel-cta-right', '16px');
    host.style.setProperty('--storeeel-cta-bottom', '16px');
    host.style.setProperty('--storeeel-cta-width', 'auto');
  } else {
    host.style.setProperty('--storeeel-cta-left', '14.738px');
    host.style.setProperty('--storeeel-cta-right', 'auto');
    host.style.setProperty('--storeeel-cta-bottom', '21px');
    host.style.setProperty('--storeeel-cta-width', '73.1%');
  }

  const shadowRoot = host.attachShadow({ mode: 'open' });
  const styleEl = document.createElement('style');
  styleEl.textContent = `
    :host {
      position: absolute;
      left: var(--storeeel-cta-left, 14.738px);
      right: var(--storeeel-cta-right, auto);
      bottom: var(--storeeel-cta-bottom, 21px);
      width: var(--storeeel-cta-width, 73.1%);
      z-index: 10;
      display: block;
      /* 外枠はタップを受けず、見えている CTA ボタン（.ctaFrame）だけで受ける。
         CTA 文言が無い等で .ctaFrame が visibility:hidden の間も外枠は箱のまま残るため、
         外枠で受けると見えない CTA の帯に重なったタップが「CTA へのタップ」として無視され、
         前後の移動や下ドラッグで閉じる操作が効かなくなる。visibility:hidden の要素はタップを受けないため裏のメディアに届く。 */
      pointer-events: none;
      /* メディア要素（applyMobileMediaLayout の translate3d）と同様に CTA も合成レイヤーへ昇格させる。
         iOS Safari / 一部 Android では translate3d を持つ要素が z-index を無視して前面に描画される
         （= CTA が画像に覆われて消える）ため、CTA 自身もレイヤー化して重なり順を安定させる。 */
      transform: translateZ(0);
      -webkit-transform: translateZ(0);
    }

    .ctaFrame {
      pointer-events: auto;
      visibility: ${(ctaText && !shouldHideUntilMediaReady) ? 'visible' : 'hidden'};
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 18px 16px 18px 18px;
      background: var(--storeeel-cta-background, var(--Black-80, rgba(39, 39, 39, 0.80)));
      border-radius: 6px;
      border: 2px solid var(--collection-1-white-100);
      box-sizing: border-box;
    }

    /* CTAテキストは手動改行を許可し、省略しない */
    .ctaFrame .text-wrapper {
      white-space: pre-wrap;
      word-wrap: break-word;
    }

    .text-wrapper {
      color: var(--storeeel-cta-text-color, var(--White-100, #FFF));
      font-size: var(--storeeel-cta-font-size, ${DEFAULT_CTA_FONT_SIZE_PX}px);
      font-style: normal;
      font-weight: 700;
      line-height: 130%;
      width: 85%;
    }

    .icon {
      width: 24px;
      height: 24px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      transform: rotate(180deg);
      color: var(--storeeel-cta-text-color, var(--White-100, #FFF));
    }

    .ctaFrame[data-has-cta-link="false"] .icon {
      display: none;
    }
  `;

  // CTA フレーム本体
  const frame = document.createElement('div');
  frame.className = 'ctaFrame';
  frame.dataset.hasCtaText = ctaText ? 'true' : 'false';
  frame.setAttribute('ctaLink', ctaLink);
  // フィードフォルダ商品の mediaAsset.id は merchant_products.id（安定ID）を合成しているため、
  // 通常content同様 content.mediaAsset?.id をそのまま使える（frequencyInsertContentId相当の別フィールドは不要）
  frame.setAttribute('mediaId', content.isFrequencyInsertContent ? (content.frequencyInsertContentId || '') : (content.mediaAsset?.id || ''));
  frame.setAttribute('mediaName', content.mediaAsset?.name || '');
  frame.setAttribute('mediaAssetType', content.mediaAsset?.type || '');
  frame.setAttribute('isFrequencyInsertContent', content.isFrequencyInsertContent ? 'true' : 'false');
  frame.setAttribute('isFeedFolderContent', content.isFeedFolderContent ? 'true' : 'false');
  applyCtaLinkIconVisibility(ctaLink, frame);

  // テキスト部分
  const textWrapper = document.createElement('div');
  textWrapper.className = 'text-wrapper';
  textWrapper.textContent = ctaText;
  applyCtaStyleToElements(content, frame, textWrapper);

  // アイコン部分（外部画像に依存せず常時表示させる）
  const icon = document.createElement('span');
  icon.className = 'icon';
  icon.innerHTML = `
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M17.0298 21.5303C17.3227 21.2375 17.3227 20.7626 17.0298 20.4697L8.5604 12.0003L17.0298 3.53082C17.3227 3.23793 17.3227 2.76307 17.0298 2.47018C16.737 2.1773 16.2621 2.1773 15.9692 2.47018L6.96946 11.4699C6.67657 11.7628 6.67657 12.2377 6.96946 12.5306L15.9692 21.5303C16.2621 21.8232 16.737 21.8232 17.0298 21.5303Z" fill="currentColor"/>
    </svg>
  `;

  // 組み立て
  frame.appendChild(textWrapper);
  frame.appendChild(icon);

  shadowRoot.appendChild(styleEl);
  shadowRoot.appendChild(frame);

  return host;
};
